import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Company } from 'types/orga'
import { Academy } from 'types/traiwiss'

interface FlightDeckContextProps {
    company: Company | null
    changeCompany: (company: Company | null) => unknown

    academy: Academy | null
    changeAcademy: (academy: Academy | null) => unknown

    openFlightDeck: () => void
}

interface InternalFlightDeckContextProps {
    setFlightDeckOpen: (open: boolean) => void
    flightDeckDialogOpen: boolean
}

const FlightDeckContext = React.createContext<FlightDeckContextProps | null>(null)
const InternalFlightDeckContext = React.createContext<InternalFlightDeckContextProps | null>(null)

interface FlightDeckProviderProps {
    children: React.ReactNode
}

export function FlightDeckProvider({ children }: FlightDeckProviderProps) {
    const [company, setCompany] = useState<Company | null>(getLocalStorageRecoveryFunction('company'))
    const [academy, setAcademy] = useState<Academy | null>(getLocalStorageRecoveryFunction('academy'))

    const [flightDeckDialogOpen, setFlightDeckDialogOpen] = useState(false);

    const openFlightDeck = useCallback(() => {
        setFlightDeckDialogOpen(true)
    }, [setFlightDeckDialogOpen])

    const changeCompany = useCallback((company: Company | null) => {
        setCompany(company)
        saveToLocalStorage('company', JSON.stringify(company))
    }, [])

    const changeAcademy = useCallback((academy: Academy | null) => {
        setAcademy(academy)
        saveToLocalStorage('academy', JSON.stringify(academy))
    }, [])

    const setFlightDeckOpen = useCallback((open: boolean) => {
        setFlightDeckDialogOpen(open)
    }, [setFlightDeckDialogOpen])

    const value = useMemo<FlightDeckContextProps>(
        () => ({ company, changeCompany, academy, changeAcademy, openFlightDeck }),
        [academy, changeAcademy, changeCompany, company, openFlightDeck],
    )

    const InternalValue = useMemo<InternalFlightDeckContextProps>(
        () => ({ setFlightDeckOpen, flightDeckDialogOpen }),
        [setFlightDeckOpen, flightDeckDialogOpen]
    )

    return <FlightDeckContext.Provider value={value}><InternalFlightDeckContext.Provider value={InternalValue}>{children}</InternalFlightDeckContext.Provider></FlightDeckContext.Provider>
}

export function useFlightDeck() {
    const value = useContext(FlightDeckContext)

    if (value === null) {
        throw new Error('You forgot to use <FlightDeckProvider />, shame on you!')
    }

    return value
}

export function useInternalFlightDeck() {
    const value = useContext(InternalFlightDeckContext)

    if (value == null) {
        throw new Error("your forgot to use <InternalFlightDeckProvider />, shame on you!")
    }

    return value
}

function saveToLocalStorage(key: string, value: string) {
    localStorage.setItem(`flightdeck_${key}`, value)
}

function getLocalStorageRecoveryFunction(key: string) {
    return () => {
        const data = localStorage.getItem(`flightdeck_${key}`)
        return data ? JSON.parse(data) : null
    }
}
